import React, { useEffect, useState } from "react";
import { BarChart3, TrendingUp, Clock, Euro, Building2, Ticket, ListChecks, AlertTriangle, Mail, MessageCircle, Globe, PenLine, Timer, ArrowLeft, ChevronRight, ListTodo, Flag } from "lucide-react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { Avatar } from "../components/Avatar";

const CH = { email: { l: "Email", i: Mail, c: "#3B82F6" }, chat: { l: "Chat", i: MessageCircle, c: "#10B981" }, widget: { l: "Widget", i: Globe, c: "#A78BFA" }, manual: { l: "Manuel", i: PenLine, c: "#F59E0B" } };
const TSTAT = { todo: { l: "À faire", c: "#A1A1AA" }, in_progress: { l: "En cours", c: "#3B82F6" }, pending_validation: { l: "Validation", c: "#F59E0B" }, validated: { l: "Validé", c: "#10B981" }, refused: { l: "Refusé", c: "#E60000" }, open: { l: "Ouvert", c: "#3B82F6" }, answered: { l: "Répondu", c: "#F59E0B" }, converted: { l: "Converti", c: "#10B981" }, closed: { l: "Fermé", c: "#A1A1AA" }, auto_closed: { l: "Fermé auto", c: "#E60000" } };
const PRI = { high: "#FF3B3B", urgent: "#FF3B3B", medium: "#3B82F6", low: "#A1A1AA" };
const TTYPE = { support: "Support", design: "Design", dev: "Dév", content: "Contenu", admin: "Admin", other: "Autre" };
function fmtPickup(s) {
  if (!s) return "—";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m`;
  return `${Math.floor(s)}s`;
}
function fmtHandle(s) {
  if (!s) return "—";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}j ${h}h`;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m}m`;
}
function fmtClock(s) { const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60); return `${h}h ${String(m).padStart(2, "0")}m`; }
function fmtD(iso) { return iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" }) : "—"; }
function Pill({ s }) { const c = TSTAT[s] || TSTAT.todo; return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap" style={{ color: c.c, background: c.c + "18" }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: c.c }} />{c.l}</span>; }

function monthOptions() {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) });
  }
  return out;
}

export default function ReportsPage() {
  const { managed } = useCompany();
  const months = monthOptions();
  const [cid, setCid] = useState("");
  const [month, setMonth] = useState(months[0].value);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => { if (!cid && managed[0]) setCid(managed[0].id); }, [managed, cid]);
  useEffect(() => { if (cid) load(); }, [cid, month]);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/companies/${cid}/report?month=${month}`); setReport(data); }
    finally { setLoading(false); }
  };
  const openMember = async (uid) => {
    setLoadingDetail(true); setDetail({ loading: true });
    try { const { data } = await api.get(`/companies/${cid}/members/${uid}/detail`); setDetail(data); }
    catch { setDetail(null); }
    finally { setLoadingDetail(false); }
  };

  return (
    <div className="relative h-full overflow-y-auto bg-background">
      <div className="p-6 lg:p-10 max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div><h1 className="font-cabinet font-bold text-2xl tracking-tight">Rapport d'activité</h1><p className="text-sm text-zinc-500">Temps travaillé, montants à régler & marge par mois</p></div>
          <div className="flex gap-2">
            <select data-testid="report-company" value={cid} onChange={(e) => setCid(e.target.value)} className="border border-white/10 rounded-md px-3 py-2 text-sm bg-card focus:outline-none focus:ring-2 focus:ring-klein/20">
              {managed.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select data-testid="report-month" value={month} onChange={(e) => setMonth(e.target.value)} className="border border-white/10 rounded-md px-3 py-2 text-sm bg-card focus:outline-none focus:ring-2 focus:ring-klein/20 capitalize">
              {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        </div>

        {managed.length === 0 ? (
          <div className="bg-card border border-white/10 rounded-md p-12 text-center text-zinc-400"><Building2 className="mx-auto mb-3" /><p className="text-sm">Créez une entreprise pour voir les rapports.</p></div>
        ) : loading ? <div className="p-12 text-center text-zinc-400">Chargement…</div> : report ? (
          <>
            {report.is_manager && report.totals && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Stat icon={Euro} label="CA client" value={`${report.totals.client_budget.toFixed(0)} €`} color="#002FA7" />
                <Stat icon={Clock} label="Coût réalisation" value={`${report.totals.internal_budget.toFixed(0)} €`} color="#A1A1AA" />
                <Stat icon={TrendingUp} label="Marge" value={`${report.totals.margin.toFixed(0)} €`} color="#10B981" />
                <Stat icon={Euro} label="À régler aux membres" value={`${report.totals.member_due.toFixed(0)} €`} color="#F59E0B" />
              </div>
            )}
            {report.is_manager && report.channels && Object.keys(report.channels).length > 0 && (
              <div className="mb-6">
                <div className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Tickets reçus par canal</div>
                <div className="flex flex-wrap gap-3" data-testid="report-channels">
                  {Object.entries(CH).map(([k, meta]) => {
                    const n = report.channels[k] || 0;
                    const Icon = meta.i;
                    return (
                      <div key={k} className="flex items-center gap-2.5 bg-card border border-white/10 rounded-xl px-4 py-2.5 min-w-[120px]">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: meta.c + "1a", color: meta.c }}><Icon size={16} /></div>
                        <div><div className="font-cabinet font-bold text-lg leading-none">{n}</div><div className="text-[11px] text-zinc-500">{meta.l}</div></div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10 bg-background font-cabinet font-semibold text-sm flex items-center gap-2"><BarChart3 size={16} /> Performance par membre</div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead><tr className="border-b border-white/10">{["Membre", "Tickets traités", "Prise en charge moy.", "Tâches faites", "En retard", "Heures", "À régler", ""].map((h) => <th key={h} className="px-4 py-2.5 font-mono-label text-[11px] uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>)}</tr></thead>
                  <tbody>
                    {report.members.map((m) => (
                      <tr key={m.user_id} data-testid={`report-row-${m.user_id}`} onClick={() => openMember(m.user_id)} className="border-b border-white/[0.06] hover:bg-white/[0.03] cursor-pointer transition-colors group">
                        <td className="px-4 py-3"><div className="flex items-center gap-2.5"><Avatar name={m.name} color="#27272A" size={30} /><div><div className="font-semibold text-sm">{m.name}</div><div className="text-[11px] text-zinc-500">{m.role}</div></div></div></td>
                        <td className="px-4 py-3 text-sm"><span className="inline-flex items-center gap-1.5 font-mono-label"><Ticket size={13} className="text-klein" /> {m.tickets_handled ?? 0}</span></td>
                        <td className="px-4 py-3 text-sm font-mono-label"><span className="inline-flex items-center gap-1.5"><Timer size={13} className="text-zinc-500" /> {fmtPickup(m.avg_pickup_seconds)}</span></td>
                        <td className="px-4 py-3 text-sm"><span className="inline-flex items-center gap-1.5 font-mono-label text-emerald-400"><ListChecks size={13} /> {m.tasks_done ?? 0}</span></td>
                        <td className="px-4 py-3 text-sm">{m.tasks_late > 0 ? <span className="inline-flex items-center gap-1.5 font-mono-label text-signal"><AlertTriangle size={13} /> {m.tasks_late}</span> : <span className="text-zinc-600 font-mono-label">0</span>}</td>
                        <td className="px-4 py-3 text-sm font-mono-label text-zinc-400">{m.hours} h</td>
                        <td className="px-4 py-3 text-sm font-mono-label font-bold text-klein">{m.amount_due} €</td>
                        <td className="px-4 py-3"><ChevronRight size={16} className="text-zinc-600 group-hover:text-klein transition-colors" /></td>
                      </tr>
                    ))}
                    {report.members.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-zinc-400">Aucune activité ce mois-ci.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-xs text-zinc-500 mt-3 flex items-center gap-1.5"><ChevronRight size={12} /> Cliquez sur un membre pour voir le détail complet de ses tâches et tickets.</p>
          </>
        ) : null}
      </div>
      {detail && <MemberDetail data={detail} loading={loadingDetail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function MemberDetail({ data, loading, onClose }) {
  if (loading || data.loading) {
    return <div className="absolute inset-0 z-40 bg-background flex items-center justify-center text-zinc-400">Chargement…</div>;
  }
  const { member, tasks, tickets, totals } = data;
  return (
    <div className="absolute inset-0 z-40 bg-background overflow-y-auto animate-in fade-in duration-200" data-testid="member-detail">
      <div className="sticky top-0 z-10 h-14 border-b border-white/10 bg-card/90 backdrop-blur flex items-center gap-3 px-4 lg:px-8">
        <button data-testid="member-detail-back" onClick={onClose} className="flex items-center gap-2 text-sm font-medium text-zinc-300 hover:text-foreground"><ArrowLeft size={18} /> Retour</button>
        <div className="flex items-center gap-2.5 ml-2">
          <Avatar name={member.name} color="#27272A" size={32} />
          <div><div className="font-cabinet font-bold text-base leading-none">{member.name}</div><div className="text-[11px] text-zinc-500">{member.role}{member.hourly_rate ? ` · ${member.hourly_rate} €/h` : ""}</div></div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={ListTodo} label="Tâches assignées" value={totals.tasks_count} color="#3B82F6" />
          <Stat icon={Clock} label="Temps tâches" value={fmtClock(totals.tasks_seconds)} color="#A78BFA" />
          <Stat icon={Ticket} label="Tickets traités" value={totals.tickets_count} color="#10B981" />
          <Stat icon={Euro} label="Coût traitement tickets" value={`${totals.tickets_cost.toFixed(0)} €`} color="#F59E0B" />
        </div>

        {/* Tasks table */}
        <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 bg-background font-cabinet font-semibold text-sm flex items-center gap-2"><ListTodo size={16} className="text-klein" /> Tâches ({tasks.length})</div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead><tr className="border-b border-white/10">{["Réf", "Tâche", "Type", "Statut", "Prio", "Client", "Créée", "Démarrée", "Terminée", "Deadline", "Temps", "Budget"].map((h) => <th key={h} className="px-3 py-2.5 font-mono-label text-[10px] uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {tasks.map((t, i) => (
                  <tr key={i} className="border-b border-white/[0.06] hover:bg-white/[0.02]">
                    <td className="px-3 py-2.5 font-mono-label text-[11px] text-zinc-400 whitespace-nowrap">{t.ref}</td>
                    <td className="px-3 py-2.5 font-medium max-w-[180px] truncate">{t.title}</td>
                    <td className="px-3 py-2.5 text-[11px] text-zinc-400 whitespace-nowrap">{TTYPE[t.task_type] || "Autre"}</td>
                    <td className="px-3 py-2.5"><Pill s={t.status} /></td>
                    <td className="px-3 py-2.5"><Flag size={13} style={{ color: PRI[t.priority] || PRI.medium }} fill={PRI[t.priority] || PRI.medium} fillOpacity={0.25} /></td>
                    <td className="px-3 py-2.5 text-[12px] text-zinc-300 max-w-[120px] truncate">{t.client_name || "—"}</td>
                    <td className="px-3 py-2.5 text-[11px] text-zinc-400 whitespace-nowrap">{fmtD(t.created_at)}</td>
                    <td className="px-3 py-2.5 text-[11px] text-zinc-400 whitespace-nowrap">{fmtD(t.started_at)}</td>
                    <td className="px-3 py-2.5 text-[11px] text-zinc-400 whitespace-nowrap">{fmtD(t.resolved_at)}</td>
                    <td className="px-3 py-2.5 text-[11px] whitespace-nowrap">{t.deadline ? <span className="text-amber-400">{fmtD(t.deadline)}</span> : <span className="text-zinc-600">—</span>}</td>
                    <td className="px-3 py-2.5 font-mono-label text-[11px] text-zinc-300 whitespace-nowrap">{fmtClock(t.time_spent_seconds)}</td>
                    <td className="px-3 py-2.5 font-mono-label text-[11px] font-bold text-klein whitespace-nowrap">{t.member_budget} €</td>
                  </tr>
                ))}
                {tasks.length === 0 && <tr><td colSpan={12} className="px-4 py-8 text-center text-zinc-500">Aucune tâche assignée.</td></tr>}
              </tbody>
              {tasks.length > 0 && <tfoot><tr className="bg-background font-semibold"><td colSpan={10} className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider text-zinc-500">Totaux</td><td className="px-3 py-2.5 font-mono-label text-[11px] text-zinc-200">{fmtClock(totals.tasks_seconds)}</td><td className="px-3 py-2.5 font-mono-label text-[11px] text-klein font-bold">{totals.tasks_member_cost} €</td></tr></tfoot>}
            </table>
          </div>
        </div>

        {/* Tickets table */}
        <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 bg-background font-cabinet font-semibold text-sm flex items-center gap-2"><Ticket size={16} className="text-klein" /> Tickets traités ({tickets.length})</div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead><tr className="border-b border-white/10">{["Réf", "Sujet", "Canal", "Statut", "Client", "Reçu", "1ère réponse", "Tps de traitement", "Coût"].map((h) => <th key={h} className="px-3 py-2.5 font-mono-label text-[10px] uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {tickets.map((t, i) => {
                  const ch = CH[t.source] || CH.manual; const Icon = ch.i;
                  return (
                    <tr key={i} className="border-b border-white/[0.06] hover:bg-white/[0.02]">
                      <td className="px-3 py-2.5 font-mono-label text-[11px] text-zinc-400 whitespace-nowrap">{t.ref}</td>
                      <td className="px-3 py-2.5 font-medium max-w-[180px] truncate">{t.subject}</td>
                      <td className="px-3 py-2.5"><span className="inline-flex items-center gap-1 text-[11px]" style={{ color: ch.c }}><Icon size={12} /> {ch.l}</span></td>
                      <td className="px-3 py-2.5"><Pill s={t.status} /></td>
                      <td className="px-3 py-2.5 text-[12px] text-zinc-300 max-w-[120px] truncate">{t.client_name || "—"}</td>
                      <td className="px-3 py-2.5 text-[11px] text-zinc-400 whitespace-nowrap">{fmtD(t.created_at)}</td>
                      <td className="px-3 py-2.5 text-[11px] text-zinc-400 whitespace-nowrap">{fmtPickup(t.pickup_seconds)}</td>
                      <td className="px-3 py-2.5 font-mono-label text-[11px] text-amber-400 whitespace-nowrap">{fmtHandle(t.handling_seconds)}</td>
                      <td className="px-3 py-2.5 font-mono-label text-[11px] font-bold text-klein whitespace-nowrap">{t.cost} €</td>
                    </tr>
                  );
                })}
                {tickets.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-zinc-500">Aucun ticket traité.</td></tr>}
              </tbody>
              {tickets.length > 0 && <tfoot><tr className="bg-background font-semibold"><td colSpan={7} className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider text-zinc-500">Totaux</td><td className="px-3 py-2.5 font-mono-label text-[11px] text-amber-400">{fmtClock(totals.tickets_seconds)}</td><td className="px-3 py-2.5 font-mono-label text-[11px] text-klein font-bold">{totals.tickets_cost} €</td></tr></tfoot>}
            </table>
          </div>
          {(!member.hourly_rate) && <p className="px-4 py-2.5 text-[11px] text-zinc-500 border-t border-white/10">💡 Définissez un taux horaire pour ce membre (dans Entreprises) pour calculer le coût de traitement des tickets.</p>}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-card border border-white/10 rounded-md p-4">
      <div className="flex items-center gap-2 mb-2"><div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: color + "18", color }}><Icon size={15} /></div><span className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400">{label}</span></div>
      <div className="font-cabinet font-bold text-2xl" style={{ color }}>{value}</div>
    </div>
  );
}
